import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Send, Copy, Plus, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';
import { streamCoachChat, copyText } from '../../lib/coachApi';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useCoachT } from '../../lib/coachI18n';

const Bubble = ({ m, t, i }) => (
  <div className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`coach-chat-msg-${i}`}>
    <div className={`max-w-[88%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-[#0F2A22] text-[#F4EBD0]' : 'bg-white border border-border'}`}>
      {m.content || <span className="text-muted-foreground italic">{t('coachThinking')}</span>}
      {m.role === 'assistant' && m.content && (
        <button
          type="button"
          className="mt-1.5 flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
          onClick={async () => { if (await copyText(m.content)) toast.success(t('copied')); else toast.error(t('error')); }}
          data-testid={`coach-chat-copy-${i}`}
        >
          <Copy className="w-3 h-3" />{t('copy')}
        </button>
      )}
    </div>
  </div>
);

/* Tab "Scrivi con il Coach": chat multi-turno per email, WhatsApp, obiezioni */
export const CoachChat = ({ leadId }) => {
  const { t, lang } = useCoachT();
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [ready, setReady] = useState(false);
  const endRef = useRef(null);
  const streamingRef = useRef(false);

  const load = useCallback(async (sid) => {
    try {
      const r = await api.get(`/coach/leads/${leadId}/chat`, { params: sid ? { session_id: sid } : {} });
      if (streamingRef.current) return;
      setSessions(r.data.sessions);
      setSessionId(r.data.session_id);
      setMessages(r.data.messages);
    } catch {
      if (!streamingRef.current) setMessages([]);
    } finally {
      setReady(true);
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming || !ready) return;
    setInput('');
    setStreaming(true);
    streamingRef.current = true;
    setMessages((prev) => [...prev, { role: 'user', content: msg }, { role: 'assistant', content: '' }]);
    try {
      const sid = await streamCoachChat({
        leadId, message: msg, sessionId, lang,
        onDelta: (d) => setMessages((prev) => {
          const copy = [...prev];
          const last = copy[copy.length - 1];
          if (last?.role !== 'assistant') return [...copy, { role: 'assistant', content: d }];
          copy[copy.length - 1] = { ...last, content: last.content + d };
          return copy;
        }),
      });
      if (sid && sid !== sessionId) {
        setSessionId(sid);
        const r = await api.get(`/coach/leads/${leadId}/chat`, { params: { session_id: sid } });
        setSessions(r.data.sessions);
      }
    } catch (e) {
      toast.error(e.message);
      setMessages((prev) => (prev[prev.length - 1]?.role === 'assistant' && !prev[prev.length - 1].content ? prev.slice(0, -1) : prev));
    } finally {
      streamingRef.current = false;
      setStreaming(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="coach-chat">
      <div className="flex items-center gap-2">
        {sessions.length > 0 && (
          <Select value={sessionId || ''} onValueChange={(v) => { setReady(false); setSessionId(v); load(v); }} disabled={streaming}>
            <SelectTrigger className="h-8 text-xs flex-1" data-testid="coach-chat-session-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {sessions.map((s) => <SelectItem key={s.session_id} value={s.session_id}>{s.title || s.session_id.slice(0, 8)}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Button size="sm" variant="outline" disabled={streaming} onClick={() => { setSessionId(null); setMessages([]); }} data-testid="coach-chat-new"><Plus className="w-3.5 h-3.5 mr-1" />{t('newChat')}</Button>
      </div>
      <div className="h-80 overflow-y-auto rounded-md bg-[var(--surface-2)] p-3 space-y-2.5" data-testid="coach-chat-messages">
        {messages.map((m, i) => <Bubble key={m.id || i} m={m} t={t} i={i} />)}
        {!messages.length && (
          <div className="flex flex-wrap gap-2" data-testid="coach-chat-chips">
            {t('chips').map((c, i) => (
              <button key={c} type="button" onClick={() => send(c)} className="text-xs rounded-full border border-[#C9A227]/50 bg-white px-3 py-1.5 hover:bg-[#C9A227]/10 transition-colors" data-testid={`coach-chip-${i}`}>{c}</button>
            ))}
          </div>
        )}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2">
        <Textarea
          rows={2} value={input} placeholder={t('chatPh')} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          className="text-sm" data-testid="coach-chat-input"
        />
        <Button onClick={() => send()} disabled={streaming || !ready || !input.trim()} className="self-end bg-[#0F2A22] hover:bg-[#173C31]" data-testid="coach-chat-send">
          {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
};
